import { del, get, patch, post, put } from './methods';
import type { CreateRequest } from './types';

/**
 * Create an API method. This abstraction is useful to create more specific
 * API function calls with declared input/output types.
 * @example
 * const getUser = createAPIMethod<{ id: string }, { name: string }>({
 *   method: 'GET',
 *   url: '/user',
 * });
 *
 * // This has fully typed parameters and response.
 * const user = await getUser({ id: '1' });
 */
export const createRequest: CreateRequest = (opts) => async (input) => {
  let method;

  switch (opts.method) {
    case 'GET':
      method = get;
      break;
    case 'POST':
      method = post;
      break;
    case 'DELETE':
      method = del;
      break;
    case 'PUT':
      method = put;
      break;
    case 'PATCH':
      method = patch;
      break;
    default:
      method = get;
  }

  const curl = requestToCurl(
    opts.url,
    opts.method ?? 'GET',
    input,
    opts.fetchOptions?.headers as Record<string, string>,
  );

  if (opts.debug) {
    console.log('--------------------------------------------------');
    console.log(curl);
    console.log('--------------------------------------------------');
  }

  const response = await method(opts.url, input ?? {}, opts.fetchOptions ?? {});
  const status = response.status;

  // Clone the response to avoid stream lock if json fails.
  const clonedResponse = response.clone();

  try {
    const data = await response.json();
    return { data, status, headers: response.headers, ok: response.ok };
  } catch (_) {
    const data = await clonedResponse.text();
    return { data, status, headers: clonedResponse.headers, ok: clonedResponse.ok };
  }
};

function requestToCurl(
  url: string,
  method: string,
  body?: unknown,
  headers?: Record<string, string>,
): string {
  let curl = [`curl -X ${method}`];

  if (headers) {
    for (const [key, value] of Object.entries(headers)) {
      curl.push(`-H '${key}: ${value}'`);
    }
  }

  if (body && method !== 'GET') {
    curl.push(`-d '${JSON.stringify(body)}'`);
  }

  curl.push(`'${url}'`);

  return curl.join(' ');
}
